import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";

import type { BannerSeverity } from "@/client-types/service-status";
import useBackendHealth from "@/hooks/useBackendHealth";
import useServiceStatus from "@/hooks/useServiceStatus";
import { cn } from "@/lib/utils";
import { buildDismissKey } from "@/utility/bannerDismissal";
import { resolveBanner } from "@/utility/resolveBanner";

const SEVERITY_CLASSES: Record<BannerSeverity, string> = {
  outage: "bg-red-950 border-b-red-900 border-l-red-500 text-red-50",
  maintenance: "bg-amber-950 border-b-amber-900 border-l-amber-400 text-amber-50",
  info: "bg-sky-950 border-b-sky-900 border-l-sky-400 text-sky-50",
};

function readDismissal(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    // Private mode or blocked storage — treat the banner as not dismissed.
    return false;
  }
}

function storeDismissal(key: string) {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    // Nothing to do: the banner just reappears on the next page load.
  }
}

const ServiceBanner = () => {
  const status = useServiceStatus();
  const backendUp = useBackendHealth();
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  const banner = resolveBanner(status, backendUp);
  if (!banner) return null;

  const dismissKey = buildDismissKey(banner);
  if (banner.dismissible && (dismissedKey === dismissKey || readDismissal(dismissKey))) return null;

  const dismiss = () => {
    storeDismissal(dismissKey);
    setDismissedKey(dismissKey);
  };

  return (
    <div role="status" aria-live="polite" className={cn("flex items-start gap-3 border-b border-l-4 border-solid px-4 py-3", SEVERITY_CLASSES[banner.severity])}>
      <div className="flex-1">
        <p className="m-0 font-semibold">{banner.title}</p>
        <p className="m-0 mt-1 text-sm opacity-90">{banner.message}</p>
        {banner.updatedAt && <p className="m-0 mt-1 text-xs opacity-70">Opdateret {banner.updatedAt}</p>}
      </div>
      {banner.dismissible && (
        <IconButton size="small" onClick={dismiss} aria-label="Luk besked" sx={{ color: "inherit" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

export default ServiceBanner;
